import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Alert,
  Paper,
  Divider,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  Stack,
  IconButton,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  People as PeopleIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Palette as PaletteIcon,
  Check as CheckIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DashboardConfig, ApiResponse, DashboardTheme } from '../types';
import DashboardPreview from '../components/DashboardPreview';
import { dashboardApi } from '../utils/api';
import { themePresets } from '../utils/themeUtils';

const DefaultConfigPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [defaultConfig, setDefaultConfig] = useState<DashboardConfig | null>(null);
  const [currentTheme, setCurrentTheme] = useState<DashboardTheme | null>(null);
  const [customPresets, setCustomPresets] = useState<any[]>([]);
  const [allThemePresets, setAllThemePresets] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [defaultThemeInfo, setDefaultThemeInfo] = useState<any>(null);
  const [settingDefaultTheme, setSettingDefaultTheme] = useState(false);
  const loginLogoInputRef = React.useRef<HTMLInputElement>(null);
  const dashboardLogoInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    // add role == "admin" if you want admins to change default config
    if (user?.role === 'superuser') {
      fetchDefaultConfig();
      loadDefaultThemeInfo();
      loadCurrentUserTheme();
    }
  }, [user]);

  const loadCurrentUserTheme = async () => {
    try {
      const config = await dashboardApi.getConfig();
      setCurrentTheme(config.theme);
      
      // Load custom presets from the user's config
      const customThemePresets = config.customThemePresets || [];
      setCustomPresets(customThemePresets);
      
      // Combine predefined and custom presets - custom themes first
      const combined = [...customThemePresets, ...themePresets];
      setAllThemePresets(combined);
    } catch (error) {
      console.error('Error loading current theme:', error);
    }
  };

  const loadDefaultThemeInfo = async () => {
    try {
      const defaultTheme = await dashboardApi.getDefaultTheme();
      setDefaultThemeInfo(defaultTheme);
    } catch (error) {
      // Ignore error if no default theme is set
      setDefaultThemeInfo(null);
    }
  };

  const fetchDefaultConfig = async () => {
    try {
      setLoading(true);
      console.log('Fetching default template...');
      
      const response = await fetch('/api/users/default-template', {
        credentials: 'include',
      });
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data: ApiResponse<DashboardConfig> = await response.json();
      console.log('Response data:', data);
      
      if (data.success && data.data) {
        // Ensure tabs structure exists for backwards compatibility
        let configData = data.data;
        if (!configData.tabs || configData.tabs.length === 0) {
          // Convert old groups structure to new tab structure
          const defaultTab = {
            id: 'default-tab',
            name: 'Home',
            icon: 'home',
            color: configData.theme?.primaryColor || '#1976d2',
            order: 1,
            groups: configData.groups || []
          };
          
          configData = {
            ...configData,
            tabs: [defaultTab],
            activeTabId: defaultTab.id,
            groups: [] // Clear old groups as they're now in the tab
          };
        }
        setDefaultConfig(configData);
      } else {
        setError(data.message || 'Failed to fetch default configuration');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setError(`Error fetching default configuration: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const saveDefaultConfig = async () => {
    if (!defaultConfig) return;
    
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      const response = await fetch('/api/users/default-template', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          name: 'Default User Template',
          description: 'Default dashboard configuration for new users',
          configData: defaultConfig,
        }),
      });

      const data: ApiResponse = await response.json();
      
      if (data.success) {
        console.log('Default configuration saved successfully with globalSettings:', defaultConfig?.globalSettings);
        setSuccess('Default configuration saved successfully! New users will now see this setup.');
      } else {
        setError(data.message || 'Failed to save default configuration');
      }
    } catch (error) {
      setError('Error saving default configuration');
    } finally {
      setSaving(false);
    }
  };

  const resetToCurrentTemplate = async () => {
    try {
      setLoading(true);
      console.log('Fetching current user dashboard config...');
      
      // Use the current user's configuration as the new default
      const response = await fetch('/api/dashboard/config', {
        credentials: 'include',
      });
      
      console.log('Dashboard config response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data: ApiResponse<{config: DashboardConfig}> = await response.json();
      console.log('Dashboard config data:', data);
      
      if (data.success && data.data && data.data.config) {
        // Ensure tabs structure exists for backwards compatibility
        let configData = data.data.config;
        if (!configData.tabs || configData.tabs.length === 0) {
          // Convert old groups structure to new tab structure
          const defaultTab = {
            id: 'default-tab',
            name: 'Home',
            icon: 'home',
            color: configData.theme?.primaryColor || '#1976d2',
            order: 1,
            groups: configData.groups || []
          };
          
          configData = {
            ...configData,
            tabs: [defaultTab],
            activeTabId: defaultTab.id,
            groups: [] // Clear old groups as they're now in the tab
          };
        }
        setDefaultConfig(configData);
        setSuccess('Template reset to your current dashboard configuration');
      } else {
        setError('Failed to load your current configuration: ' + (data.message || 'Invalid data structure'));
      }
    } catch (error) {
      console.error('Error loading current configuration:', error);
      setError(`Error loading current configuration: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Logo management functions
  const handleLogoUpload = async (type: 'login' | 'dashboard', event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !defaultConfig) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      setError('Please select a valid image file (PNG, JPEG, GIF, WebP, or SVG)');
      return;
    }
    
    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setError('Image file must be smaller than 5MB');
      return;
    }

    try {
      // Convert image to base64 for storage
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target?.result as string;
        
        // Update the default config with the new logo
        const updatedConfig: DashboardConfig = {
          ...defaultConfig,
          globalSettings: {
            ...defaultConfig.globalSettings,
            [type === 'login' ? 'loginLogo' : 'dashboardLogo']: {
              type: 'image',
              url: base64String
            }
          }
        };
        
        setDefaultConfig(updatedConfig);
        console.log('Logo updated, new config:', updatedConfig);
        setSuccess(`${type === 'login' ? 'Login' : 'Dashboard'} logo updated successfully! Don't forget to click "Save as Default Template" to persist changes.`);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading logo:', error);
      setError('Error uploading logo. Please try again.');
    }
    
    // Reset the input
    if (type === 'login' && loginLogoInputRef.current) {
      loginLogoInputRef.current.value = '';
    } else if (type === 'dashboard' && dashboardLogoInputRef.current) {
      dashboardLogoInputRef.current.value = '';
    }
  };

  const handleResetLogo = (type: 'login' | 'dashboard') => {
    if (!defaultConfig) return;

    const updatedConfig: DashboardConfig = {
      ...defaultConfig,
      globalSettings: {
        ...defaultConfig.globalSettings,
        [type === 'login' ? 'loginLogo' : 'dashboardLogo']: {
          type: 'default'
        }
      }
    };
    
    setDefaultConfig(updatedConfig);
    setSuccess(`${type === 'login' ? 'Login' : 'Dashboard'} logo reset to default!`);
  };

  const handleSetDefaultTheme = async () => {
    try {
      setSettingDefaultTheme(true);
      setError('');
      await dashboardApi.setDefaultTheme('Default theme for new users - set from admin panel');
      setSuccess('Current theme set as default for new users!');
      await loadDefaultThemeInfo();
    } catch (error: any) {
      setError(error.message || 'Failed to set default theme');
    } finally {
      setSettingDefaultTheme(false);
    }
  };

  const handleApplyPreset = async (presetId: string) => {
    try {
      setError('');
      const preset = allThemePresets.find(p => p.id === presetId);
      if (preset) {
        // Update the user's actual theme configuration
        const config = await dashboardApi.getConfig();
        const updatedConfig = {
          ...config,
          theme: preset.theme
        };
        await dashboardApi.updateConfig(updatedConfig);
        
        // Update local state for immediate preview
        setCurrentTheme(preset.theme);
        setSuccess(`Applied "${preset.name}" theme. You can now set it as default for new users.`);
      }
    } catch (error: any) {
      setError(error.message || 'Failed to apply theme preset');
    }
  };

  const isCurrentTheme = (presetTheme: DashboardTheme) => {
    if (!currentTheme) return false;
    return (
      presetTheme.mode === currentTheme.mode &&
      presetTheme.primaryColor === currentTheme.primaryColor &&
      presetTheme.secondaryColor === currentTheme.secondaryColor &&
      presetTheme.backgroundColor === currentTheme.backgroundColor &&
      presetTheme.cardBackground === currentTheme.cardBackground &&
      presetTheme.textColor === currentTheme.textColor
    );
  };
  // if you want admins to access it change it here
  if (user?.role !== 'superuser') {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          Access denied. Only administrators can manage default configurations.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header with Navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard')}
            variant="outlined"
          >
            Back to Dashboard
          </Button>
          <Box>
            <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <SettingsIcon fontSize="large" />
              Default User Configuration
            </Typography>
          </Box>
        </Box>
        
        <Button
          startIcon={<PeopleIcon />}
          onClick={() => navigate('/users')}
          variant="outlined"
          color="primary"
        >
          User Management
        </Button>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="body1" color="text.secondary">
          Customize the default dashboard template that will be applied to all new user accounts.
          Changes made here will only affect newly created users, not existing ones.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Controls */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Template Management
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={saveDefaultConfig}
                disabled={saving || !defaultConfig}
                color="primary"
              >
                {saving ? 'Saving...' : 'Save as Default Template'}
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={resetToCurrentTemplate}
                disabled={loading}
              >
                Use My Current Dashboard
              </Button>
              
              <Button
                variant="outlined"
                onClick={fetchDefaultConfig}
                disabled={loading}
              >
                Reload Template
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Global Logo Settings */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Global Logo Settings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Upload custom logos for the login page and dashboard header. These will be used across the entire application.
            </Typography>
            
            <Grid container spacing={3}>
              {/* Login Page Logo */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ImageIcon fontSize="small" />
                    Login Page Logo
                  </Typography>
                  
                  {/* Current Logo Preview */}
                  <Box sx={{ 
                    mb: 2, 
                    p: 2, 
                    border: '1px dashed #ccc', 
                    borderRadius: 1, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    minHeight: 100,
                    backgroundColor: '#f9f9f9'
                  }}>
                    {defaultConfig?.globalSettings?.loginLogo?.type === 'image' && defaultConfig.globalSettings.loginLogo.url ? (
                      <img
                        src={defaultConfig.globalSettings.loginLogo.url}
                        alt="Login Logo Preview"
                        style={{
                          maxHeight: 80,
                          maxWidth: '100%',
                          objectFit: 'contain'
                        }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Using default logo
                      </Typography>
                    )}
                  </Box>
                  
                  <input
                    ref={loginLogoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml"
                    style={{ display: 'none' }}
                    onChange={(e) => handleLogoUpload('login', e)}
                  />
                  
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<CloudUploadIcon />}
                      onClick={() => loginLogoInputRef.current?.click()}
                      disabled={saving}
                    >
                      Upload Logo
                    </Button>
                    
                    {defaultConfig?.globalSettings?.loginLogo?.type === 'image' && (
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleResetLogo('login')}
                        disabled={saving}
                      >
                        Reset to Default
                      </Button>
                    )}
                  </Box>
                </Card>
              </Grid>
              
              {/* Dashboard Header Logo */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ImageIcon fontSize="small" />
                    Dashboard Header Logo
                  </Typography>
                  
                  {/* Current Logo Preview */}
                  <Box sx={{ 
                    mb: 2, 
                    p: 2, 
                    border: '1px dashed #ccc', 
                    borderRadius: 1, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    minHeight: 100,
                    backgroundColor: '#f9f9f9'
                  }}>
                    {defaultConfig?.globalSettings?.dashboardLogo?.type === 'image' && defaultConfig.globalSettings.dashboardLogo.url ? (
                      <img
                        src={defaultConfig.globalSettings.dashboardLogo.url}
                        alt="Dashboard Logo Preview"
                        style={{
                          maxHeight: 80,
                          maxWidth: '100%',
                          objectFit: 'contain'
                        }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Using default logo
                      </Typography>
                    )}
                  </Box>
                  
                  <input
                    ref={dashboardLogoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml"
                    style={{ display: 'none' }}
                    onChange={(e) => handleLogoUpload('dashboard', e)}
                  />
                  
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<CloudUploadIcon />}
                      onClick={() => dashboardLogoInputRef.current?.click()}
                      disabled={saving}
                    >
                      Upload Logo
                    </Button>
                    
                    {defaultConfig?.globalSettings?.dashboardLogo?.type === 'image' && (
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleResetLogo('dashboard')}
                        disabled={saving}
                      >
                        Reset to Default
                      </Button>
                    )}
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Default Theme Settings */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PaletteIcon />
              Default Theme for New Users
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Choose a theme preset or use your customized theme as the default that new users will inherit.
            </Typography>

            {/* Theme Preset Picker - Same layout as ThemePresets component */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>Choose Theme:</Typography>
              <Grid container spacing={3}>
                {allThemePresets.map((preset: any) => (
                  <Grid item xs={12} sm={6} md={4} key={preset.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        border: isCurrentTheme(preset.theme) ? 2 : 1,
                        borderColor: isCurrentTheme(preset.theme) ? 'primary.main' : 'divider',
                        position: 'relative',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: (theme) => theme.shadows[4],
                        },
                      }}
                    >
                      {/* Current Theme Indicator */}
                      {isCurrentTheme(preset.theme) && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 1,
                            backgroundColor: 'primary.main',
                            borderRadius: '50%',
                            width: 24,
                            height: 24,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <CheckIcon sx={{ fontSize: 16, color: 'white' }} />
                        </Box>
                      )}

                      <CardContent sx={{ flexGrow: 1 }}>
                        {/* Theme Preview */}
                        <Box
                          sx={{
                            height: 120,
                            borderRadius: 2,
                            mb: 2,
                            backgroundColor: preset.theme.backgroundColor,
                            border: '1px solid #e0e0e0',
                            position: 'relative',
                            overflow: 'hidden',
                          }}
                        >
                          {/* Background Pattern */}
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: `linear-gradient(135deg, ${preset.theme.cardBackground} 20%, ${preset.theme.backgroundColor} 80%)`,
                            }}
                          />

                          {/* Sample Card */}
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 16,
                              left: 16,
                              right: 16,
                              height: 40,
                              backgroundColor: preset.theme.cardBackground,
                              borderRadius: preset.theme.borderRadius / 8,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              display: 'flex',
                              alignItems: 'center',
                              px: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: preset.theme.primaryColor,
                                mr: 1,
                              }}
                            />
                            <Box
                              sx={{
                                width: 20,
                                height: 4,
                                backgroundColor: preset.theme.textColor,
                                opacity: 0.7,
                                borderRadius: 2,
                              }}
                            />
                          </Box>

                          {/* Color Dots */}
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 8,
                              left: 8,
                              display: 'flex',
                              gap: 0.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: '50%',
                                backgroundColor: preset.theme.primaryColor,
                                border: '1px solid white',
                              }}
                            />
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: '50%',
                                backgroundColor: preset.theme.secondaryColor,
                                border: '1px solid white',
                              }}
                            />
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: '50%',
                                backgroundColor: preset.theme.customColors.accent1,
                                border: '1px solid white',
                              }}
                            />
                          </Box>

                          {/* Mode Icon */}
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              color: preset.theme.textColor,
                            }}
                          >
                            {preset.theme.mode === 'dark' ? <DarkModeIcon /> : <LightModeIcon />}
                          </Box>
                        </Box>

                        {/* Theme Info */}
                        <Stack spacing={1}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                              {preset.name}
                            </Typography>
                            {preset.isCustom && (
                              <Chip 
                                label="Custom" 
                                size="small" 
                                color="secondary" 
                                variant="outlined"
                              />
                            )}
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40 }}>
                            {preset.description}
                          </Typography>

                          {/* Theme Properties */}
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip 
                              label={preset.theme.mode === 'dark' ? 'Dark Mode' : 'Light Mode'} 
                              size="small" 
                              variant="outlined"
                              icon={preset.theme.mode === 'dark' ? <DarkModeIcon /> : <LightModeIcon />}
                            />
                            <Chip 
                              label={`${preset.theme.borderRadius}px radius`} 
                              size="small" 
                              variant="outlined"
                            />
                          </Box>
                        </Stack>
                      </CardContent>

                      <CardActions>
                        <Button
                          variant={isCurrentTheme(preset.theme) ? 'contained' : 'outlined'}
                          fullWidth
                          onClick={() => handleApplyPreset(preset.id)}
                          disabled={isCurrentTheme(preset.theme)}
                        >
                          {isCurrentTheme(preset.theme) ? 'Current Theme' : 'Apply Theme'}
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Current Default Theme Status */}
            {defaultThemeInfo ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Current Default Theme:</strong> New users inherit this theme on registration.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Last updated: {new Date(defaultThemeInfo.setAt).toLocaleDateString()} • 
                  {defaultThemeInfo.description}
                </Typography>
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  No default theme set. New users will use the system default light theme.
                </Typography>
              </Alert>
            )}

            {/* Selected Theme Preview */}
            {currentTheme ? (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Selected Theme Preview:</Typography>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  p: 2, 
                  border: '2px solid', 
                  borderColor: 'primary.main', 
                  borderRadius: 2,
                  alignItems: 'center',
                  backgroundColor: 'action.hover'
                }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: currentTheme.primaryColor,
                        border: '1px solid white',
                        boxShadow: 1,
                      }}
                    />
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: currentTheme.secondaryColor,
                        border: '1px solid white',
                        boxShadow: 1,
                      }}
                    />
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: currentTheme.customColors.accent1,
                        border: '1px solid white',
                        boxShadow: 1,
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {allThemePresets.find(p => isCurrentTheme(p.theme))?.name || 'Custom Theme'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {currentTheme.mode === 'dark' ? 'Dark' : 'Light'} mode • 
                      Border radius: {currentTheme.borderRadius}px
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box sx={{ mb: 3, p: 2, backgroundColor: 'action.hover', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Loading your current theme...
                </Typography>
              </Box>
            )}

            {/* Action Button */}
            <Button
              variant="contained"
              color="secondary"
              startIcon={<PaletteIcon />}
              onClick={handleSetDefaultTheme}
              disabled={settingDefaultTheme || !currentTheme}
            >
              {settingDefaultTheme ? 'Setting Default Theme...' : 'Set Current Theme as Default for New Users'}
            </Button>
          </Paper>
        </Grid>

        {/* Preview */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Preview - New User Dashboard
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {loading ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">Loading default configuration...</Typography>
                </Box>
              ) : defaultConfig ? (
                <Box sx={{ 
                  border: '2px dashed #e0e0e0', 
                  borderRadius: 2, 
                  p: 3, 
                  backgroundColor: '#fafafa',
                  minHeight: 400
                }}>
                  <DashboardPreview 
                    config={defaultConfig} 
                    onConfigChange={setDefaultConfig}
                    isEditable={true}
                  />
                </Box>
              ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    No default configuration loaded. Click "Reload Template" to load the current default.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Instructions */}
      <Paper sx={{ p: 3, mt: 3, backgroundColor: '#f8f9fa' }}>
        <Typography variant="h6" gutterBottom>
          How to Use
        </Typography>
        <Typography variant="body2" component="div">
          <ol>
            <li><strong>Edit the preview above</strong> - Add, remove, or modify groups and tiles</li>
            <li><strong>Customize themes and layout</strong> - Change colors, spacing, and appearance</li>
            <li><strong>Save as Default Template</strong> - Apply your changes to new user accounts</li>
            <li><strong>Alternative:</strong> Set up your personal dashboard exactly how you want it, then click "Use My Current Dashboard"</li>
          </ol>
          <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
            Note: Existing users will keep their current configurations. Only newly created accounts will use this template.
          </Typography>
        </Typography>
      </Paper>
    </Container>
  );
};

export default DefaultConfigPage;